import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, Mail } from 'lucide-react';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Field from '@/components/forms/Field';
import CodeInput from '@/components/forms/CodeInput';

import { loginRequest, loginVerify } from '@/lib/publicApi';
import { saveSession, isLoggedIn } from '@/lib/auth';
import { ApiError } from '@/lib/api';

type Step = 'email' | 'code';

function useCountdown(from: string | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!from) return 0;
  return Math.max(0, Math.ceil((new Date(from).getTime() - now) / 1000));
}

export default function Login() {
  const navigate = useNavigate();
  useEffect(() => {
    if (isLoggedIn()) navigate('/cabinet', { replace: true });
  }, [navigate]);

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [nextResendAt, setNextResendAt] = useState<string | null>(null);
  const resendSeconds = useCountdown(nextResendAt);

  const topRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [step]);

  const handleSend = async (evt?: React.FormEvent) => {
    evt?.preventDefault();
    setError(null);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setEmailError('Введите корректный email');
      return;
    }
    setEmailError(null);
    setSending(true);
    try {
      const res = await loginRequest(email.trim().toLowerCase());
      setNextResendAt(res.nextAt);
      setCode('');
      setStep('code');
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Не удалось отправить код. Попробуйте позже.');
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async (codeToSend?: string) => {
    const c = (codeToSend ?? code).trim();
    if (c.length < 4) return;
    setVerifying(true);
    setError(null);
    try {
      const session = await loginVerify(email.trim().toLowerCase(), c);
      saveSession(session);
      navigate('/cabinet', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Не удалось подтвердить. Попробуйте ещё раз.');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <>
      <Header />
      <main className="section-divider bg-paper">
        <div className="container-site py-16 sm:py-24" ref={topRef}>
          <div className="mx-auto max-w-xl">
            <div className="border border-line bg-white p-6 sm:p-10">
              {step === 'email' ? (
                <form onSubmit={handleSend} noValidate>
                  <span className="eyebrow">Вход</span>
                  <h1 className="mt-4 text-[28px] font-medium leading-tight tracking-tightest text-ink">
                    Личный кабинет
                  </h1>
                  <p className="mt-3 text-[14px] leading-relaxed text-ink/60">
                    Введите email, который указали при регистрации.
                    Мы отправим 6-значный код для входа.
                  </p>

                  <div className="mt-8">
                    <Field
                      label="Email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      inputMode="email"
                      placeholder="you@company.ru"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (emailError) setEmailError(null);
                      }}
                      error={emailError}
                      autoFocus
                    />
                  </div>

                  {error && (
                    <div className="mt-4 border border-orange bg-orange/5 px-4 py-3 text-[13px] text-ink">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={sending}
                    className="mt-6 btn-primary w-full justify-between disabled:opacity-60"
                  >
                    <span>{sending ? 'Отправляем код…' : 'Получить код'}</span>
                    <ArrowUpRight size={16} strokeWidth={1.75} />
                  </button>

                  <p className="mt-6 text-center text-[13px] text-ink/60">
                    Нет аккаунта?{' '}
                    <Link to="/signup" className="link">
                      Зарегистрируйтесь
                    </Link>
                  </p>
                </form>
              ) : (
                <div>
                  <button
                    type="button"
                    onClick={() => setStep('email')}
                    className="inline-flex items-center gap-2 text-sm text-ink/60 hover:text-ink transition"
                  >
                    <ArrowLeft size={16} strokeWidth={1.6} />
                    Изменить email
                  </button>
                  <h1 className="mt-6 text-[28px] font-medium leading-tight tracking-tightest text-ink">
                    Введите код
                  </h1>
                  <p className="mt-3 flex items-center gap-2 text-[14px] text-ink/60">
                    <Mail size={14} strokeWidth={1.6} /> Отправили на {email.toLowerCase()}
                  </p>

                  <div className="mt-8">
                    <CodeInput
                      value={code}
                      onChange={(v) => {
                        setCode(v);
                        if (error) setError(null);
                      }}
                      onComplete={(v) => handleVerify(v)}
                      autoFocus
                      disabled={verifying}
                      error={!!error}
                    />
                    {error && <p className="mt-3 text-[13px] text-orange">{error}</p>}
                  </div>

                  <button
                    type="button"
                    onClick={() => handleVerify()}
                    disabled={verifying || code.length < 4}
                    className="mt-8 btn-primary w-full justify-between disabled:opacity-60"
                  >
                    <span>{verifying ? 'Проверяем…' : 'Войти'}</span>
                    <ArrowUpRight size={16} strokeWidth={1.75} />
                  </button>

                  <div className="mt-6 flex items-center justify-between text-[13px] text-ink/60">
                    <span>Не пришёл код?</span>
                    <button
                      type="button"
                      onClick={() => handleSend()}
                      disabled={resendSeconds > 0 || sending}
                      className="link disabled:no-underline disabled:opacity-50"
                    >
                      {resendSeconds > 0
                        ? `Повторно через ${resendSeconds} с`
                        : sending
                          ? 'Отправляем…'
                          : 'Отправить повторно'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
